import _ from 'lodash';
import { useCallback } from 'react';
import { PostLinkAccess } from '../graphql/generated/graphql';
import useAllGroups from '../groups/useAllGroups';
import type { UserRole } from '../topic/topicPolicyCheck';
import topicPolicyCheck, {
  isFullUser,
  isRegisteredUser,
  TopicPolicyAction,
} from '../topic/topicPolicyCheck';
import { useAllTopicMap } from '../topic/useAllTopics';
import { useAllUsersMap } from '../user/useAllUsers';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import type { TopicPolicyCheck_UserFragment } from '../graphql/generated/graphql';

export enum PostPolicyAction {
  EditPost,
  ViewPost,
}

type PostShareActions = PostPolicyAction.EditPost | PostPolicyAction.ViewPost;

gql(`
  fragment postPolicyCheck_post_excludeTopics on SlimPostFields {
    linkAccess
    publishedAt
    ... on Post {
      owner {
        id
      }
      sharing {
        editorIds
        viewerIds
        editorGroupIds
        viewerGroupIds
      }
      verification {
        id
      }
    }
  }
`);

const IS_ALLOWED_THROUGH_LINK_ACCESS_POST_FRAGMENT = gql(`
  fragment isAllowedThroughLinkAccess_post on RootPostFields {
    linkAccess
    ... on SlimPostFields {
      publishedAt
    }
  }
`);

const IS_ALLOWED_THROUGH_GROUP_SHARING_POST_FRAGMENT = gql(`
  fragment isAllowedThroughGroupSharing_post on Post {
    id
    sharing {
      editorGroupIds
      viewerGroupIds
    }
  }
`);

const IS_ALLOWED_THROUGH_GROUP_SHARING_USER_FRAGMENT = gql(`
  fragment isAllowedThroughGroupSharing_user on User {
    id
    membershipGroups {
      id
    }
  }
`);

const POST_POLICY_CHECK_POST_FRAGMENT = gql(`
  fragment postPolicyCheck_post on SlimPostFields {
    id
    ...postPolicyCheck_post_excludeTopics
    ...isAllowedThroughLinkAccess_post
    ...isAllowedThroughGroupSharing_post
    ... on Post {
      topics {
        id
        ...topicPolicyCheck_topic
      }
    }
  }
`);

type PostPolicyCheck_PostFragment = DocumentType<
  typeof POST_POLICY_CHECK_POST_FRAGMENT
>;

const POST_POLICY_CHECK_PUBLIC_POST_FRAGMENT = gql(`
  fragment postPolicyCheck_publicPost on PublicPost {
    id
    ...isAllowedThroughLinkAccess_post
  }
`);

type PostPolicyCheck_PublicPostFragment = DocumentType<
  typeof POST_POLICY_CHECK_PUBLIC_POST_FRAGMENT
>;

type User =
  | UserRole
  | (DocumentType<typeof IS_ALLOWED_THROUGH_GROUP_SHARING_USER_FRAGMENT> &
      TopicPolicyCheck_UserFragment);

export function isAllowedThroughLinkAccess(
  action: PostShareActions,
  post: DocumentType<typeof IS_ALLOWED_THROUGH_LINK_ACCESS_POST_FRAGMENT>,
  user: User,
): boolean {
  const isRegisteredUserOnDraft =
    'publishedAt' in post && !post.publishedAt && isRegisteredUser(user);

  switch (post.linkAccess) {
    case PostLinkAccess.Disabled:
      return false;
    case PostLinkAccess.Internal:
      return isRegisteredUserOnDraft;
    case PostLinkAccess.Public:
      return isRegisteredUserOnDraft || action === PostPolicyAction.ViewPost;
    case PostLinkAccess.PublicEdit:
      return (
        action === PostPolicyAction.ViewPost ||
        action === PostPolicyAction.EditPost
      );
    default:
      return false;
  }
}

const isAllowedThroughDirectSharing: (
  action: PostShareActions,
  post: PostPolicyCheck_PostFragment,
  user: User,
) => boolean = (action, post, user) =>
  !!(
    'sharing' in post &&
    typeof user === 'object' &&
    user.id &&
    (post.sharing?.editorIds?.includes(user.id) ||
      (action === PostPolicyAction.ViewPost &&
        post.sharing?.viewerIds?.includes(user.id)))
  );

export const isAllowedThroughTopicSharing: (
  action: PostShareActions,
  post: PostPolicyCheck_PostFragment,
  user: User,
) => boolean = (action, post, user) => {
  if (!('publishedAt' in post)) {
    return false;
  }
  const topicAction =
    action === PostPolicyAction.EditPost
      ? TopicPolicyAction.EditPost
      : TopicPolicyAction.ViewPost;

  return (
    'topics' in post &&
    !!post.topics &&
    !!post.publishedAt &&
    ((post.topics.length === 0 && isFullUser(user)) ||
      post.topics.some(
        (topic) => topic && topicPolicyCheck(topicAction, topic, user),
      ))
  );
};

const isAllowedThroughGroupSharing = (
  action: PostShareActions,
  post:
    | DocumentType<typeof IS_ALLOWED_THROUGH_GROUP_SHARING_POST_FRAGMENT>
    | PostPolicyCheck_PublicPostFragment,
  user: User,
) =>
  'sharing' in post && typeof user === 'object'
    ? _.intersection(
        [
          ...(post.sharing?.editorGroupIds ?? []),
          ...(action === PostPolicyAction.ViewPost
            ? post.sharing?.viewerGroupIds ?? []
            : []),
        ],
        user.membershipGroups?.map((g) => g.id) ?? [],
      ).length > 0
    : false;

function postPolicyCheck(
  action: PostPolicyAction,
  post: PostPolicyCheck_PostFragment | PostPolicyCheck_PublicPostFragment,
  user: User,
): boolean {
  return (
    // Checking owner until all usages are migrated to TypeScript
    (typeof user === 'object' &&
      'owner' in post &&
      post.owner?.id === user.id) ||
    isAllowedThroughLinkAccess(action, post, user) ||
    ('sharing' in post && isAllowedThroughDirectSharing(action, post, user)) ||
    ('topics' in post && isAllowedThroughTopicSharing(action, post, user)) ||
    ('sharing' in post && isAllowedThroughGroupSharing(action, post, user))
  );
}

export default postPolicyCheck;

const IS_VERIFIER_POST_FRAGMENT = gql(`
  fragment isVerifier_post on Post {
    verification {
      id
      primaryVerifier {
        id
      }
      secondaryVerifiers {
        id
      }
    }
  }
`);

export function isVerifier(
  post: DocumentType<typeof IS_VERIFIER_POST_FRAGMENT>,
  user: { id: string },
) {
  return !!(
    post.verification?.primaryVerifier.id === user.id ||
    post.verification?.secondaryVerifiers.some((v) => v?.id === user.id)
  );
}

const USE_POST_POLICY_CHECK_POST_FRAGMENT = gql(`
  fragment usePostPolicyCheck_post on SlimPostFields {
    id
    topics {
      id
    }
    ...postPolicyCheck_post_excludeTopics
  }
`);

export const usePostPolicyCheck = () => {
  const topicMap = useAllTopicMap();
  const userMap = useAllUsersMap();
  const groups = useAllGroups();

  return useCallback(
    (
      action: PostPolicyAction,
      basePost: DocumentType<typeof USE_POST_POLICY_CHECK_POST_FRAGMENT>,
      baseUser: UserRole | { id: string },
    ) => {
      const topics = _.compact(
        basePost?.topics.map((t) =>
          t.__typename === 'RedactedTopic' ? t : topicMap[t.id],
        ),
      );
      const post = { ...basePost, topics };

      const user =
        typeof baseUser === 'object' ? userMap[baseUser.id] : baseUser;

      return (
        !!post &&
        !!user &&
        postPolicyCheck(
          action,
          post,
          typeof user === 'object'
            ? {
                ...user,
                membershipGroups: groups.filter((group) =>
                  group.allUsers.map((g) => g.id).includes(user.id),
                ),
              }
            : user,
        )
      );
    },
    [topicMap, userMap, groups],
  );
};
